import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { defaultIsReturnable, type ChallanType } from "@steelbill/types";
import { api } from "@/lib/api";
import {
  endpoints,
  type ChallanDetail,
  type DocumentPreview,
  type PreviewEnvelope,
} from "@/lib/endpoints";
import { text, usableApiLines, type EditorLine } from "@/features/invoices/use-invoice-editor";
// `?editing=` is the same parameter with the same meaning on both editors, so it
// is built by one function — see its note for what it costs to omit.
import { editingQuery } from "@/features/quotations/use-quotation-editor";

/**
 * The delivery-challan editor's state and its wire format (§17.7, Rule 55 CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS SHARES WITH THE OTHER TWO EDITORS, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * SHARED: everything about a LINE. `EditorLine`, `text`, `usableApiLines` and
 * `toApiLine` are IMPORTED from `@/features/invoices/use-invoice-editor`, not
 * reimplemented, because all three create routes validate lines with the same
 * `DocumentLineInput` schema (§4) — the document kinds differ in their header,
 * never in a line. Two copies of that mapping would drift the first time a field
 * was added to one kind and not the other.
 *
 * NOT SHARED: the header. A challan carries the LEGAL BASIS for a movement of
 * goods — why they are travelling, in which direction, on which lorry, whether
 * they are coming back and when. None of that is meaningful on an offer or a
 * bill, and a merged state object would put "Vehicle no." on a quotation.
 *
 * AND, AS EVERYWHERE: NO TOTAL IS COMPUTED HERE. Every figure comes from
 * `POST /challans/preview` (CLAUDE.md, §6). A challan carries a taxable value
 * because Rule 55(1) requires one; none of it is ever output tax, and nothing in
 * this file may present it as a sum anyone pays.
 * ═════════════════════════════════════════════════════════════════════════════
 */

export interface ChallanEditorState {
  customerId: number | null;
  /** `YYYY-MM-DD`. The day the goods MOVED — it decides the rates (§8.7) and FY. */
  date: string;
  /**
   * WHY THE GOODS ARE MOVING (Rule 55(1)) — and `null` UNTIL THE OWNER SAYS SO.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * THIS IS THE ONE FIELD ON THE FORM WITH NO SAFE DEFAULT, and modelling it as
   * nullable is the whole point rather than a nicety.
   *
   * `challanDirection` derives INWARD from `JOB_WORK_IN` and OUTWARD from
   * everything else, and the direction decides which address block prints as
   * CONSIGNER and which as CONSIGNEE. Rule 55(1) requires both parties named, so
   * a wrong type does not produce an untidy document — it produces a FALSE one,
   * printing the receiver as the sender of goods that are already on a lorry.
   *
   * Seeding the select with `JOB_WORK_OUT` would therefore put "goods sent for
   * job work" on a record of a customer's material coming IN, and it would do so
   * silently, on the paper travelling with the goods, for anyone who did not
   * think to change a field that already looked answered.
   * `ChallanCreateBaseSchema` gives it no default for exactly this reason, and
   * this state must not quietly reintroduce one. {@link toChallanApiBody}
   * returns `null` while it is unset, so neither the preview nor the save can
   * fire on a guess.
   * ═════════════════════════════════════════════════════════════════════════
   */
  challanType: ChallanType | null;
  /**
   * Whether the goods are expected back. Seeded from the type by
   * {@link withChallanType} and then whatever the owner says — the galvaniser
   * sometimes keeps the offcuts, and he should not have to argue with the form
   * about it.
   */
  isReturnable: boolean;
  /** `YYYY-MM-DD`, or blank. Meaningless — and refused — unless `isReturnable`. */
  expectedReturnDate: string;
  /** The lorry, as typed. Never decorated: no "Vehicle: " baked into the value. */
  vehicleNumber: string;
  /**
   * Where the goods are physically going when that is NOT the customer's
   * registered address — a galvaniser's workshop, a site. BLANK IS THE ORDINARY
   * CASE and means "the address printed above"; never synthesise a placeholder.
   */
  shipToAddress: string;
  /** "Kind Attn to:" on the consignee block. */
  attnName: string;
  notes: string;
  terms: string;
  lines: EditorLine[];
}

/**
 * A fresh, empty challan.
 *
 * `challanType: null` IS THE POINT — see the field's note. The screen must render
 * the type select FIRST and unanswered, because it is the field a wrong guess
 * would falsify the printed page with, and because `isReturnable` below has no
 * defensible value until it is answered.
 *
 * `isReturnable: false` here is therefore a placeholder for an unasked question,
 * not an assertion. {@link withChallanType} replaces it with
 * `defaultIsReturnable(type)` the moment the type is chosen, and until then
 * `toChallanApiBody` refuses to build a body at all, so the value never crosses
 * the wire.
 */
export function emptyChallanState(today: string, lines: EditorLine[]): ChallanEditorState {
  return {
    customerId: null,
    date: today,
    challanType: null,
    isReturnable: false,
    expectedReturnDate: "",
    vehicleNumber: "",
    shipToAddress: "",
    attnName: "",
    notes: "",
    terms: "",
    lines,
  };
}

/**
 * Choose the movement's type, and re-seed what follows from it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TYPE RE-SEEDS `isReturnable` INSTEAD OF LEAVING IT ALONE. Only
 * `SUPPLY_NOT_INVOICED` is a one-way trip; everything else comes back —
 * processed material from the galvaniser, the customer's own material once
 * fabricated, goods the customer declined, tools. `defaultIsReturnable` is the
 * server's own answer to that, and applying it here means the checkbox the owner
 * is looking at says the same thing the API would have assumed.
 *
 * Left alone, switching to `SUPPLY_NOT_INVOICED` would carry a ticked "expected
 * back" onto a delivery that is never coming back, which puts the challan in the
 * OVERDUE list a week later — and that list is one of the two §17.7 was built
 * for. Re-seeding only on an EXPLICIT type change is the narrow version of this:
 * an owner who then unticks the box keeps his answer.
 *
 * It also clears `expectedReturnDate` when the new type is not returnable,
 * because the pair is otherwise a 400 — see {@link withIsReturnable}.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function withChallanType(
  state: ChallanEditorState,
  challanType: ChallanType,
): ChallanEditorState {
  return withIsReturnable({ ...state, challanType }, defaultIsReturnable(challanType));
}

/**
 * Tick or untick "these goods are coming back", keeping the pair consistent.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UNTICKING MUST CLEAR THE DATE, AND THE COST OF FORGETTING IS NOT A BAD SAVE —
 * IT IS A FORM THAT NEVER WORKS AGAIN.
 *
 * `ChallanCreateSchema.superRefine` rejects `isReturnable: false` sent together
 * with an `expectedReturnDate` ("Goods that are not coming back cannot have a
 * return date"). Because `toChallanApiBody` feeds BOTH the debounced preview and
 * the save, an inconsistent pair does not merely fail on the Save button: it
 * makes every preview 400, so the totals bar sits in an error state for the whole
 * session with no obvious cause. Keeping the invariant in the state transition
 * rather than in the request builder is what stops the screen from reaching that
 * state at all.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function withIsReturnable(
  state: ChallanEditorState,
  isReturnable: boolean,
): ChallanEditorState {
  return {
    ...state,
    isReturnable,
    expectedReturnDate: isReturnable ? state.expectedReturnDate : "",
  };
}

/**
 * An existing challan, back into the strings the editor holds — the "edit" path.
 *
 * THERE IS NO COPY PATH, AND THERE WILL NOT BE ONE. `routes/paths.ts` declares no
 * `challanCopy` on purpose: every movement is its own event, with its own date,
 * its own lorry and its own reason. A copied challan would carry last week's
 * vehicle number to today's gate, which is the one field on the document the
 * person checking it can actually verify. That is why this takes no
 * `{ copy, today }` option bag, unlike `stateFromQuotation`.
 *
 * Editing is allowed only while the challan is OPEN, and the SERVER decides that
 * — `challan.isEditable` on the response, re-asserted on the `PUT`. The movement
 * freezes the moment it closes: once the goods are back, or a tax invoice depends
 * on them, the paper is a record rather than a plan.
 */
export function stateFromChallan(
  challan: ChallanDetail,
  lines: EditorLine[],
): ChallanEditorState {
  return {
    customerId: challan.customerId,
    date: challan.date,
    challanType: challan.challanType,
    isReturnable: challan.isReturnable,
    expectedReturnDate: challan.expectedReturnDate ?? "",
    vehicleNumber: challan.vehicleNumber ?? "",
    shipToAddress: challan.shipToAddress ?? "",
    attnName: challan.attnName ?? "",
    notes: challan.notes ?? "",
    terms: challan.terms ?? "",
    lines,
  };
}

/**
 * The whole challan as the API takes it, or `null` when not yet sendable.
 *
 * `null` IS WHAT KEEPS THE PREVIEW QUIET while the form is half typed — the
 * debounced preview simply does not fire, rather than firing a 400 per keystroke
 * and painting an error over an incomplete form the owner is still filling in.
 *
 * THREE THINGS MAKE IT SENDABLE, and `challanType` is the one this document adds
 * to the usual pair. See the field's note: there is no safe guess, so an unset
 * type is "not ready", never "assume the common case".
 *
 * WHAT IS ABSENT IS AS DELIBERATE AS WHAT IS HERE. No `challanNumber` — the
 * server allocates it under the company row lock, gap-free (§6). No `status` — a
 * challan opens OPEN and advances through its own routes. No `supplyType` — it is
 * derived from the two state codes, by the same engine that decided CGST+SGST vs
 * IGST, so a client asserting it could make the printed page disagree with the
 * tax on it. And no `direction`: it is derived from `challanType`, because a
 * second copy could disagree with the first and print the wrong party as the
 * sender (§17.7).
 */
export function toChallanApiBody(state: ChallanEditorState): Record<string, unknown> | null {
  if (state.customerId === null) return null;
  if (state.date.trim() === "") return null;
  if (state.challanType === null) return null;

  const lines = usableApiLines(state.lines);
  if (lines === null) return null;

  return {
    customerId: state.customerId,
    date: state.date,
    challanType: state.challanType,
    /*
     * ALWAYS SENT, never omitted. Omitting it means "whatever this type usually
     * does", which the server resolves with `defaultIsReturnable` — correct for a
     * client that does not know the field exists, and wrong for this one, which
     * is showing the owner a tick box. What the form shows is what gets stored.
     */
    isReturnable: state.isReturnable,
    /*
     * Belt and braces beside `withIsReturnable`: a blank date is `undefined`, and
     * a date on goods that are not coming back is dropped rather than sent, since
     * the pair is a 400 that would break the preview as well as the save.
     *
     * THE OTHER CROSS-FIELD RULE IS NOT SWALLOWED HERE. An `expectedReturnDate`
     * EARLIER than `date` is also a 400 — and it is left to travel, because the
     * server's message ("'Expected back' cannot be earlier than the challan
     * date") is a real sentence the owner can act on. Silently withholding the
     * request instead would leave the totals bar merely "incomplete", with the
     * transposed pair still on screen and nothing saying which field is wrong.
     */
    expectedReturnDate: state.isReturnable ? text(state.expectedReturnDate) : undefined,
    vehicleNumber: text(state.vehicleNumber),
    shipToAddress: text(state.shipToAddress),
    attnName: text(state.attnName),
    notes: text(state.notes),
    /*
     * `text` maps a blank to `undefined`, which the PUT reads as "clear it". That
     * is right for a challan: the create prefills `company.terms_default`, and a
     * PUT sends back exactly what the editor is showing, so re-filling a cleared
     * field on the way out would undo the owner's edit.
     */
    terms: text(state.terms),
    lines,
  };
}

/**
 * The server's figures for the challan as it currently stands.
 *
 * `POST /challans/preview` WRITES NOTHING AND ALLOCATES NO NUMBER — no row, no
 * counter, no item-usage touch, no audit entry. A challan previewed twenty times
 * and never saved leaves no gap in the CHL series, which matters more here than
 * elsewhere: challan serial ranges are declared in GSTR-1 (§17.4), so a gap is a
 * question from the department (§6).
 *
 * IT NEEDS `challan.create`, NOT `challan.view` — previewing an unsaved document
 * is part of AUTHORING one, and `challan.view` is in §2.1's shared read row which
 * the ACCOUNTANT holds. Gate the button on the identical string.
 *
 * `editingId` IS THE ID FROM THE ROUTE when revising a saved challan, and the
 * figures are wrong without it — see {@link editingQuery}. §4 freezes the buyer
 * onto the saved challan, so `PUT /challans/:id` keeps the STORED snapshot while
 * this route (told nothing) read the LIVE `customers` row, and the two disagree
 * about the place of supply the moment the customer's state code is corrected.
 * Undefined for a new challan, which is the create path and unaffected.
 *
 * DEBOUNCED at 400ms, matching both other editors: long enough that typing a rate
 * does not fire six requests, short enough that the total stays attached to the
 * keystroke.
 */
export function useChallanPreview(state: ChallanEditorState, editingId?: string) {
  const body = toChallanApiBody(state);
  const [debounced, setDebounced] = useState<string | null>(null);

  const serialised = body === null ? null : JSON.stringify(body);
  const suffix = editingQuery(editingId);

  useEffect(() => {
    if (serialised === null) {
      setDebounced(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebounced(serialised);
    }, 400);
    return () => {
      window.clearTimeout(timer);
    };
  }, [serialised]);

  return useQuery({
    // The suffix is part of the key: the same lines previewed against a saved
    // challan and against a new one are two different answers.
    queryKey: ["challans", "preview", suffix, debounced],
    queryFn: () =>
      api.post<PreviewEnvelope<DocumentPreview>>(
        `${endpoints.challanPreview}${suffix}`,
        JSON.parse(debounced ?? "{}") as unknown,
      ),
    enabled: debounced !== null,
    // The last good figures stay on screen while the next preview is in flight,
    // so the total does not blink to nothing between keystrokes.
    placeholderData: keepPreviousData,
    // A rejected preview is information — the cross-field messages above are
    // exactly what the owner needs to read — not a transient failure to retry.
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * The save — `POST /challans` for a new one, `PUT /challans/:id` for a revision.
 *
 * A CHALLAN HAS A `PUT` AND AN ISSUED INVOICE DOES NOT, and that is not an
 * inconsistency between the two. An invoice is a tax document; it is corrected by
 * a credit note and `invoice.edit` does not exist as a capability for anyone
 * (§2.1, §17.3, CLAUDE.md). A challan records a PHYSICAL MOVEMENT — the lorry is
 * loaded, the count changes, and the paper has to match what actually went. The
 * server gates the route on the stored status (OPEN only), not on anything this
 * screen could talk its way past.
 *
 * A `PUT` REPLACES THE LINES WHOLESALE and never touches the number: the challan
 * keeps CHL/26-27/7 across every revision, which is what the driver is carrying.
 */
export function useSaveChallan(id: string | undefined) {
  const queryClient = useQueryClient();
  const isEdit = id !== undefined && id !== "";

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      isEdit
        ? api.put<PreviewEnvelope<ChallanDetail>>(endpoints.challan(id), body)
        : api.post<PreviewEnvelope<ChallanDetail>>(endpoints.challans, body),
    onSuccess: () => {
      /*
       * EVERYTHING, for the same reason the other two saves invalidate
       * everything: a saved challan changes its own register, the unbilled count
       * the home screen links to (`/challans?openOnly=true`), the overdue list
       * and the item usage the picker orders by. A narrower invalidation leaves
       * the owner looking at a screen that does not include the document he just
       * saved — which reads as the save having failed.
       */
      void queryClient.invalidateQueries();
    },
  });
}
